import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  useDeleteCheckMutation,
  useGetCheckQuery,
  useListChatSessionsQuery,
  useListChecksQuery,
  useListIssuesQuery,
  useStartCheckMutation,
  useStartChatMutation,
} from '@shared/api';
import { Card, CardTitle, CardSubtitle } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { ACCEPT_COMPLIANCE_DOC } from '@shared/lib/fileAccept';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Progress, Loader } from '@shared/ui/Progress';
import { ChatPanel } from '@widgets/ChatPanel/ChatPanel';
import type { ComplianceIssue, Severity } from '@entities/compliance/types';
import { complianceStatusLabel } from '@shared/lib/statusLabels';
import { riseIn } from '@shared/theme/animations';
import { useAgentPermission } from '@entities/user';

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(8)};
  animation: ${riseIn} 360ms ${({ theme }) => theme.motion.easeOut};
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.005em;
`;

const Scoreboard = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Metric = styled(Card)`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(5)};
`;

const MetricValue = styled.div<{ $tone?: Severity | 'score' }>`
  font-size: 36px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone, theme }) => {
    switch ($tone) {
      case 'critical':
        return theme.colors.status.danger;
      case 'major':
        return theme.colors.status.warning;
      case 'minor':
        return theme.colors.status.info;
      case 'score':
        return theme.colors.primary;
      default:
        return theme.colors.text.primary;
    }
  }};
`;

const MetricLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const IssueCard = styled.details<{ $index?: number }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $index = 0 }) => `${$index * 40}ms`};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &[open] {
    border-color: ${({ theme }) => theme.colors.primary}40;
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }

  & summary {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(3)};
    list-style: none;
    font-weight: 500;
    font-size: 14px;
  }
  & summary::-webkit-details-marker {
    display: none;
  }
  & summary::before {
    content: '▸';
    color: ${({ theme }) => theme.colors.text.muted};
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
    display: inline-block;
  }
  &[open] summary::before {
    transform: rotate(90deg);
  }
`;

const Quote = styled.blockquote`
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3.5)};
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 0 ${({ theme }) => theme.radius.sm} ${({ theme }) => theme.radius.sm} 0;
`;

const FixBlock = styled.div`
  background: ${({ theme }) => theme.colors.status.info}10;
  border: 1px solid ${({ theme }) => theme.colors.status.info}33;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.55;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto auto;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

const HistoryToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const HistoryCount = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ProcessingNote = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const ErrorBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  color: ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
`;

const Starting = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Notice = styled.div<{ $tone: 'warning' | 'info' }>`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.55;
  background: ${({ theme, $tone }) =>
    $tone === 'warning' ? `${theme.colors.status.warning}15` : `${theme.colors.status.info}10`};
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'warning' ? `${theme.colors.status.warning}40` : `${theme.colors.status.info}33`};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NoticeTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export function ComplianceCheck() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [startCheck, { isLoading: starting }] = useStartCheckMutation();
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await startCheck(form).unwrap();
      setActiveId(res.check_id);
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Ошибка запуска';
      setError(msg);
    }
  }

  return (
    <div>
      <Section>
        <FileDropZone
          accept={ACCEPT_COMPLIANCE_DOC}
          onFile={handleFile}
          hint="PDF, DOCX, TXT, MD и сканы — до 200 МБ"
        />
        {starting && (
          <Starting>
            <Loader /> Запуск проверки…
          </Starting>
        )}
        {error && <ErrorBox>{error}</ErrorBox>}
      </Section>

      {activeId && <CheckResult checkId={activeId} />}

      <Section>
        <SectionTitle>История проверок</SectionTitle>
        <History onSelect={setActiveId} activeId={activeId} />
      </Section>
    </div>
  );
}

function CheckResult({ checkId }: { checkId: string }) {
  const theme = useTheme();
  // Poll only while the check is in flight. As soon as it lands on
  // `completed`/`failed` we stop hammering the API.
  const probe = useGetCheckQuery(checkId);
  const probeStatus = probe.data?.status;
  const stillRunning =
    probeStatus === undefined || probeStatus === 'pending' || probeStatus === 'processing';
  const { data: check } = useGetCheckQuery(checkId, {
    pollingInterval: stillRunning ? 2000 : 0,
  });
  const isComplete = check?.status === 'completed';
  const { data: issuesData } = useListIssuesQuery({ checkId, limit: 200 }, { skip: !isComplete });

  if (!check) return null;

  if (check.status === 'pending' || check.status === 'processing') {
    return (
      <Section>
        <Card>
          <CardTitle>Идёт проверка: {check.document_name}</CardTitle>
          <CardSubtitle>Анализируем фрагменты документа в RAG-пайплайне.</CardSubtitle>
          <div style={{ marginTop: 16 }}>
            <Progress percent={check.progress_percent} />
          </div>
          <ProcessingNote>
            <Loader />
            <span>Прогресс: {check.progress_percent}%</span>
          </ProcessingNote>
        </Card>
      </Section>
    );
  }

  if (check.status === 'failed') {
    return (
      <Section>
        <Card>
          <CardTitle style={{ color: theme.colors.status.danger }}>Проверка не выполнена</CardTitle>
          <CardSubtitle>{check.error_message ?? 'Неизвестная ошибка'}</CardSubtitle>
        </Card>
      </Section>
    );
  }

  const score = check.compliance_score ?? 0;
  const scorePct = Math.round(score * 100);
  const issues = issuesData?.items ?? [];
  const outOfScope = check.summary?.out_of_scope === true;
  const zeroIssues = check.summary?.zero_issues === true;
  const coverage = check.summary?.coverage;
  const analyzed = check.summary?.analyzed_chunks;
  const totalChunks = check.summary?.total_chunks;

  return (
    <>
      <Section>
        <SectionTitle>Сводка по документу: {check.document_name}</SectionTitle>

        {outOfScope && (
          <Notice $tone="warning">
            <NoticeTitle>⚠ Документ вне зоны действия загруженной базы знаний</NoticeTitle>
            Релевантный нормативный контекст найден лишь для {analyzed ?? 0} из {totalChunks ?? 0}{' '}
            фрагментов ({coverage != null ? Math.round(coverage * 100) : 0}%). Результат
            недостоверен — добавьте подходящие ГОСТы/СП в базу знаний и повторите проверку.
          </Notice>
        )}

        {!outOfScope && zeroIssues && (
          <Notice $tone="info">
            <NoticeTitle>Нарушений не выявлено</NoticeTitle>
            Из {analyzed ?? 0} проанализированных фрагментов ни один не дал замечаний. Это либо
            реально хороший документ, либо в загруженной базе нет норм по затронутым темам — если
            сомневаетесь, расширьте базу знаний.
          </Notice>
        )}

        <Scoreboard>
          <Metric $delay={0}>
            <MetricValue $tone="score">{scorePct}%</MetricValue>
            <MetricLabel>Соответствие</MetricLabel>
          </Metric>
          <Metric $delay={60}>
            <MetricValue $tone="critical">{check.critical_issues}</MetricValue>
            <MetricLabel>Критические</MetricLabel>
          </Metric>
          <Metric $delay={120}>
            <MetricValue $tone="major">{check.major_issues}</MetricValue>
            <MetricLabel>Существенные</MetricLabel>
          </Metric>
          <Metric $delay={180}>
            <MetricValue $tone="minor">{check.minor_issues}</MetricValue>
            <MetricLabel>Формальные</MetricLabel>
          </Metric>
        </Scoreboard>

        {check.summary?.text && (
          <Card $delay={240}>
            <CardTitle>Резюме</CardTitle>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>{check.summary.text}</div>
          </Card>
        )}
      </Section>

      <Section>
        <SectionTitle>Найденные нарушения ({issues.length})</SectionTitle>
        {issues.length === 0 && (
          <Card>
            <CardSubtitle>Нарушений не выявлено.</CardSubtitle>
          </Card>
        )}
        {issues.map((it, i) => (
          <IssueItem key={it.id} issue={it} index={i} />
        ))}
      </Section>

      <Section>
        <SectionTitle>Уточнить у агента</SectionTitle>
        {/* key on checkId so sessionId state resets when user navigates between checks */}
        <ComplianceChat key={check.id} checkId={check.id} />
      </Section>
    </>
  );
}

function ComplianceChat({ checkId }: { checkId: string }) {
  const theme = useTheme();
  // Look up an existing chat session attached to this compliance check.
  // The session lives in postgres (chat_sessions table) and survives navigation;
  // we just need to discover it on mount so the user doesn't see "Open chat" again.
  const { data: existing, isLoading: looking } = useListChatSessionsQuery({
    scope: 'compliance',
    compliance_check_id: checkId,
  });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [startChat, { isLoading: starting }] = useStartChatMutation();
  const [error, setError] = useState<string | null>(null);

  // When the lookup resolves, auto-open the most recent session if any.
  useEffect(() => {
    if (sessionId) return;
    const first = existing?.items[0];
    if (first) setSessionId(first.id);
  }, [existing, sessionId]);

  async function open() {
    setError(null);
    try {
      const res = await startChat({
        scope: 'compliance',
        compliance_check_id: checkId,
      }).unwrap();
      setSessionId(res.id);
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось открыть чат';
      setError(msg);
    }
  }

  if (sessionId) {
    return <ChatPanel sessionId={sessionId} agentCode="statut" />;
  }

  if (looking) {
    return (
      <Card>
        <Loader />
      </Card>
    );
  }

  return (
    <Card>
      <CardSubtitle style={{ marginBottom: 12 }}>
        Откройте чат с агентом, чтобы переформулировать пункты документа, попросить пояснение к
        нарушению или уточнить требование норматива. Чат сохранится — следующий раз откроется сразу.
      </CardSubtitle>
      <Button onClick={open} disabled={starting}>
        {starting ? 'Создаём сессию…' : 'Открыть чат по проверке'}
      </Button>
      {error && <div style={{ marginTop: 12, color: theme.colors.status.danger, fontSize: 13 }}>{error}</div>}
    </Card>
  );
}

function IssueItem({ issue, index }: { issue: ComplianceIssue; index: number }) {
  const theme = useTheme();
  return (
    <IssueCard $index={index}>
      <summary>
        <Badge $severity={issue.severity}>{labelOf(issue.severity)}</Badge>
        {issue.category && <Badge>{issue.category}</Badge>}
        {issue.page_number && (
          <span style={{ fontSize: 12, color: theme.colors.text.muted }}>стр. {issue.page_number}</span>
        )}
        <span>{issue.issue_description}</span>
      </summary>
      <Quote>{issue.chunk_text}</Quote>
      {issue.suggested_fix && (
        <FixBlock>
          <strong>Рекомендация: </strong>
          {issue.suggested_fix}
        </FixBlock>
      )}
      {issue.referenced_chunks.length > 0 && (
        <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 8 }}>
          Ссылок на нормативные фрагменты: {issue.referenced_chunks.length}
        </div>
      )}
    </IssueCard>
  );
}

function labelOf(s: Severity): string {
  switch (s) {
    case 'critical':
      return 'Критическое';
    case 'major':
      return 'Существенное';
    case 'minor':
      return 'Формальное';
    case 'info':
      return 'Информация';
  }
}

function History({
  onSelect,
  activeId,
}: {
  onSelect: (id: string | null) => void;
  activeId: string | null;
}) {
  const { can_write } = useAgentPermission('statut');
  // Poll only while at least one check is in flight; otherwise the history is static.
  const probe = useListChecksQuery();
  const anyInFlight = !!probe.data?.items.some(
    (c) => c.status === 'pending' || c.status === 'processing',
  );
  const { data } = useListChecksQuery(undefined, {
    pollingInterval: anyInFlight ? 5_000 : 0,
  });
  const [deleteCheck, { isLoading: deleting }] = useDeleteCheckMutation();

  async function removeOne(id: string) {
    if (!window.confirm('Удалить эту проверку из истории?')) return;
    await deleteCheck(id)
      .unwrap()
      .catch(() => {});
    if (id === activeId) onSelect(null);
  }

  async function clearAll() {
    if (!data || data.items.length === 0) return;
    if (!window.confirm(`Удалить все ${data.items.length} проверок из истории?`)) return;
    // Fire all deletes in parallel — backend handles them concurrently and the
    // optimistic update on `deleteCheck` removes rows from cache immediately.
    await Promise.all(
      data.items.map((c) =>
        deleteCheck(c.id)
          .unwrap()
          .catch(() => {}),
      ),
    );
    onSelect(null);
  }

  if (!data) {
    return (
      <Card>
        <Loader />
      </Card>
    );
  }
  if (data.items.length === 0) {
    return (
      <Card>
        <CardSubtitle>Проверок ещё не было.</CardSubtitle>
      </Card>
    );
  }
  return (
    <>
      <HistoryToolbar>
        <HistoryCount>Всего проверок: {data.items.length}</HistoryCount>
        <Button
          $variant="ghost"
          $size="sm"
          disabled={deleting || !can_write}
          title={!can_write ? 'Только чтение' : undefined}
          onClick={clearAll}
        >
          Очистить историю
        </Button>
      </HistoryToolbar>
      <Card style={{ padding: '8px 24px' }}>
        {data.items.map((c) => (
          <HistoryRow key={c.id}>
            <span style={{ fontWeight: 500 }}>{c.document_name}</span>
            <Badge
              $tone={
                c.status === 'completed' ? 'success' : c.status === 'failed' ? 'danger' : 'info'
              }
            >
              {complianceStatusLabel(c.status)}
            </Badge>
            <span className="mono">
              {c.compliance_score != null ? `${Math.round(c.compliance_score * 100)}%` : '—'}
            </span>
            <Button
              $size="sm"
              $variant={c.id === activeId ? 'primary' : 'secondary'}
              onClick={() => onSelect(c.id)}
            >
              Открыть
            </Button>
            <Button
              $size="sm"
              $variant="ghost"
              disabled={deleting || !can_write}
              onClick={() => removeOne(c.id)}
              title={!can_write ? 'Только чтение' : 'Удалить запись'}
              aria-label="Удалить"
            >
              ×
            </Button>
          </HistoryRow>
        ))}
      </Card>
    </>
  );
}
